import { inject, Injectable } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { TableDataPayload, TableLazyLoad } from '@web/shared/ui';
import { map, Observable } from 'rxjs';
import { AdminWarehouseGoodApiService } from '../api/services/admin-warehouse-good-api.service';
import { AdminWarehouseGoodCollectionItem } from '../models/admin-warehouse-good-collection-item';
import { AdminWarehouseGoodCollectionFilter } from '../models/admin-warehouse-good-collection-filter';
import { Guid } from '@web/utils';
import { AdminWarehouseGoodDetails } from '../models/admin-warehouse-good-details';
import { WarehouseGoodUsage } from '../models/warehouse-good-usage';
import { WarehouseGoodConnectedObject } from '../models/warehouse-good-connected-object';

@Injectable({
  providedIn: 'root',
})
export class AdminWarehouseGoodDataSourceService {
  private readonly warehouseGoodApi = inject(AdminWarehouseGoodApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminWarehouseGoodCollectionFilter>,
  ): Observable<TableDataPayload<AdminWarehouseGoodCollectionItem>> {
    return this.warehouseGoodApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            /* prettier-ignore */
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendBoolean((builder) => builder.equalFor('promotion').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('quantityUnit').build())
              .appendText((builder) => builder.equalFor('type').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('brand').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('durabilityInformation').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('localisation').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('measure').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('quantity').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('sku').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminWarehouseGoodCollectionItem>(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminWarehouseGoodCollectionItem({
                    id: item.id,
                    type: item.type,
                    quantity: item.quantity,
                    quantityUnit: item.quantityUnit,
                    sku: item.sku,
                    name: item.name,
                    createdAt: item.createdAt,
                    brand: item.brand.label,
                    brandId: item.brand.id,
                    localization: item.localisation?.label,
                    localizationId: item.localisation?.id,
                    promotion: item.promotion,
                    durabilityInformation: item.durabilityInformation,
                    measure: item.measure,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsById(
    id: Guid,
    cacheable = false,
  ): Observable<AdminWarehouseGoodDetails> {
    return this.warehouseGoodApi.fetchDetailsById(id, cacheable).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new AdminWarehouseGoodDetails({
            id: response.id,
            brand: response.brand.label,
            brandId: response.brand.id,
            durabilityInformation: response.durabilityInformation,
            measure: response.measure,
            promotion: response.promotion,
            quantity: response.quantity,
            quantityUnit: response.quantityUnit,
            name: response.name,
            sku: response.sku,
            type: response.type,
            createdAt: response.createdAt,
            localization: response.localisation?.label,
            localizationId: response.localisation?.id,
          }),
      ),
    );
  }

  public fetchGoodUsage(goodId: Guid): Observable<WarehouseGoodUsage> {
    return this.warehouseGoodApi.fetchGoodUsage(goodId).pipe(
      map(
        (response) =>
          new WarehouseGoodUsage({
            status: response.status,
            connectedObjects: response.connectedObjects.map(
              (object) =>
                new WarehouseGoodConnectedObject({
                  id: object.id,
                  type: object.type,
                  name: object.name,
                }),
            ),
          }),
      ),
    );
  }
}
